import { z } from 'zod';
import { NonEmptyStringSchema } from './primitives';

/**
 * Every closed vocabulary in the platform, in one place.
 *
 * Each is exported twice: as a `readonly` tuple (iterate it, render it, drive a
 * state table from it) and as a Zod enum. Where CLAUDE.md and DESIGN_SPEC.md
 * disagree, CLAUDE.md wins — see docs/DECISIONS.md ZD-003.
 */

// ---------------------------------------------------------------------------
// Workflow
// ---------------------------------------------------------------------------

/**
 * Assessment states, in pipeline order (CLAUDE.md §Core conventions).
 *
 * Order matters: the state machine's transition table is validated against this
 * sequence, and `human_review_required` sits immediately before
 * `approved_for_reporting` because nothing may pass between them.
 *
 * ## `recommendations_created` was here and is deleted (docs/DECISIONS.md ZD-079)
 *
 * **Deleted because its object was refused twice on measurement, not deferred
 * because nobody has built it yet** — and the difference is why this is a
 * removal rather than a `null` producer left standing. ZD-060 measured that a
 * `Recommendation`'s every derivable byte is a function of `qrs_control_id`
 * alone (181 rows, 27 distinct bodies) and that the editorial half was 0
 * characters; ZD-062 measured that the 36 `recommendation_templates` were never
 * a content partition; ZD-079 measured the remaining route — the contextualiser
 * — and found the one sentence-shape that would have made the editorial half
 * non-zero needs to name an object the acceptance layer refuses on **40 of 40**
 * writable findings.
 *
 * So the stage had no satisfiable form but the padding ZD-060 already refused,
 * and a stage nothing can pass is not a wait: every transition writes an audit
 * event, so a stage advanced through by a worker that did nothing is a false
 * entry in the record an evidence pack exists to be.
 *
 * `control_mapping_complete` now leads straight to `qa_review_complete`.
 * `RecommendationSchema` and `ActionSchema` stay in `./treatment` with no
 * producer, exactly as ZD-060 left them.
 *
 * ## `scoring_complete` was here and is deleted (docs/DECISIONS.md ZD-093)
 *
 * **A different argument from the one above, and the difference is worth
 * keeping.** `recommendations_created` waited on an object three sessions
 * measured and refused. This stage waited on nothing: `runDomainAnalysis`
 * passes `qrsMaterialityScorer` into `validateFinding`, so a finding is banded
 * at the moment it is created and no assessment has ever held one that was not.
 * Scoring is a **step inside domain analysis**, not a stage after it — so the
 * state marked a boundary the pipeline does not have.
 *
 * Both clauses of its guard were empty, for two different reasons.
 * `findings_hiding_scoring_gaps` was empty **by construction** — the property
 * is the scorer's, and every scorer that exists satisfies it — so the rule now
 * lives where its population is, as a per-scorer conformance test in
 * `./scoring`. `unscored_finding_ids` was empty **for want of a producer**, and
 * still would be after relocation, since nothing writes it and nothing is
 * scheduled to. A guard whose only input is a fact nobody populates passes on
 * an absence rather than checking anything.
 *
 * ZD-005's refusal is therefore retired here and re-sited rather than lost —
 * see `unscoredScorer` in `./scoring`, whose doc comment named the window
 * between `domain_analysis_complete` and `scoring_complete` that this deletion
 * abolishes. What replaces it is an assertion over the run that actually
 * happens: `finding-store.test.ts` pins that the production analysis path bands
 * every finding it persists. Nothing had ever pinned that — removing the
 * scorer from `runDomainAnalysis` left all 1581 tests green.
 *
 * `domain_analysis_complete` now leads straight to `control_mapping_complete`.
 */
export const ASSESSMENT_STATES = [
  'created',
  'input_uploaded',
  'ingestion_complete',
  'clarification_required',
  'canonical_model_created',
  'domain_selection_confirmed',
  'domain_analysis_complete',
  'control_mapping_complete',
  'qa_review_complete',
  'human_review_required',
  'approved_for_reporting',
  'report_generated',
  'actions_exported',
  'closed',
  'monitoring',
] as const;
export const AssessmentStateSchema = z.enum(ASSESSMENT_STATES);
export type AssessmentState = z.infer<typeof AssessmentStateSchema>;

/**
 * What a client is told an assessment's state is, where a screen has a state
 * and no `next` to read — the landing page's list (docs/DECISIONS.md ZD-124
 * §9). The state names are the machine's own vocabulary and `canonical model
 * created` is not a sentence for a client; the third time this session an
 * internal vocabulary reached a client screen, and the third time the remedy
 * is a labelled derivation beside the enum (`DOMAIN_LABELS`, a stage's
 * `label`). No transition, no order and no *who acts next* is carried here:
 * a page that needs those reads `next`. Client-facing, so `enums.test.ts`
 * holds each to the audience rule.
 */
export const STATE_LABELS: Readonly<Record<AssessmentState, string>> = {
  created: 'Created',
  input_uploaded: 'Documents uploaded',
  ingestion_complete: 'Documents read',
  clarification_required: 'Questions for you',
  canonical_model_created: 'System modelled',
  domain_selection_confirmed: 'Scope confirmed',
  domain_analysis_complete: 'Threats analysed',
  control_mapping_complete: 'Controls mapped',
  qa_review_complete: 'Findings checked',
  human_review_required: 'Awaiting review',
  approved_for_reporting: 'Approved for reporting',
  report_generated: 'Report generated',
  actions_exported: 'Actions exported',
  closed: 'Closed',
  monitoring: 'Monitoring',
};

/**
 * Finding lifecycle (CLAUDE.md §Core conventions). Rejected findings are
 * retained, never deleted.
 *
 * **A state is derived from an `ApproverDecision`, never asserted** — see
 * `findingStateFor` in `./review` (docs/DECISIONS.md ZD-061 §1). `edited` in
 * particular is not a decision a reviewer takes: it is what `approved` becomes
 * when the decision carried a slot edit, so a finding cannot be `edited` without
 * also having been accepted.
 *
 * `deferred` is new in sds-06 and is the one member the four decided states
 * could not express: *seen, and consciously not decided*. `review_required`
 * means nobody has looked. The measurement that forced it is that **50 of the
 * corpus's 102 findings are conditional on a fact only the client can supply**,
 * and 42 of those would be settled by one answer — so a gate demanding one of
 * `approved | edited | rejected | accepted_risk` for every finding would
 * manufacture fifty decisions rather than record any, which is ZD-019's failure
 * one layer out.
 */
export const FINDING_STATES = [
  'candidate',
  'needs_clarification',
  'qa_flagged',
  'review_required',
  'deferred',
  'approved',
  'edited',
  'rejected',
  'accepted_risk',
  'client_ready',
  'archived',
] as const;
export const FindingStateSchema = z.enum(FINDING_STATES);
export type FindingState = z.infer<typeof FindingStateSchema>;

/** Stages a worker may be permitted to act in (DESIGN_SPEC §5.1). */
export const WORKER_STAGES = [
  'upload_intake',
  'interpretation',
  'normalisation',
  'analysis',
  'treatment',
  'quality_gate',
  'output',
  'integration',
] as const;
export const WorkerStageSchema = z.enum(WORKER_STAGES);
export type WorkerStage = z.infer<typeof WorkerStageSchema>;

/**
 * How a worker executes. CLAUDE.md admits `hybrid` (DESIGN_SPEC §6.1 lists only
 * two, but §6.2 classifies Control Mapping as hybrid, so three it is).
 */
export const EXECUTION_TYPES = ['deterministic', 'llm', 'hybrid'] as const;
export const ExecutionTypeSchema = z.enum(EXECUTION_TYPES);
export type ExecutionType = z.infer<typeof ExecutionTypeSchema>;

/**
 * Tools a worker contract may permit (DESIGN_SPEC §6.1, §6.3).
 *
 * **Closed, for the reason `FORBIDDEN_ACTIONS` is closed** — `WorkerContract`
 * already argues it against itself: *"a prose restriction nobody can check is
 * documentation; this is a control."* Until sds-08 the argument only bit on one
 * of the two lists, because no tool existed that anything could gate: every
 * worker was deterministic and `permitted_tools` was free text nothing read.
 * `llm_completion` is the first tool a worker can be refused, so the list stops
 * being documentation the moment the router exists (docs/DECISIONS.md ZD-046).
 *
 * Members are the tools something actually uses. `multimodal_extract` appears in
 * DESIGN_SPEC §6.3's worked ingestion contract and is deliberately **not** here:
 * nothing performs it, and adding a member nothing writes is ZD-019's failure in
 * the vocabulary rather than in a field.
 */
export const PERMITTED_TOOLS = [
  'text_parse',
  'pdf_text_extract',
  'structured_parse',
  /** A model call through the router. The only route to a provider (ZD-046). */
  'llm_completion',
] as const;
export const PermittedToolSchema = z.enum(PERMITTED_TOOLS);
export type PermittedTool = z.infer<typeof PermittedToolSchema>;

/**
 * What a model call is *for* (docs/DECISIONS.md ZD-046).
 *
 * A worker names a purpose, never a model. §13.3 tiers models by worker to
 * protect margin and latency, and a worker naming its own model would make that
 * a per-call decision scattered across the codebase rather than one table — the
 * same reasoning that makes `PlanSelector` own domain selection rather than the
 * orchestrator.
 *
 * Clarification phrasing is absent deliberately: ZD-044 makes every slot of a
 * question unauthorable, so no model writes one and there is no purpose to name.
 *
 * **Two more came out in `sds-05u`, and the rule is the same one** (ZD-080 §2):
 * a purpose here is a route to a provider, so a purpose whose worker makes no
 * call is a route to nothing. `recommendation_drafting` named the worker ZD-079
 * refused — ZD-079 deleted the stage and did not sweep this. `qa_review` named
 * the automated QA review, which `sds-05u` measured as deterministic end to
 * end: of §6.5's eight checks, six have a producer upstream, one has no subject,
 * one is a phrase list, and none of the eight is an interpretation. That is the
 * **third** row corrected in this direction after the domain workers (ZD-031,
 * ZD-063) and control mapping (ZD-059), and it repeats ZD-059's rarer half —
 * `stubs.ts` had the contract typed `deterministic` all along, so the code was
 * right and the specification was stale.
 */
export const LLM_PURPOSES = [
  'artefact_extraction',
  'finding_contextualisation',
  'identity_judgement',
] as const;
export const LlmPurposeSchema = z.enum(LLM_PURPOSES);
export type LlmPurpose = z.infer<typeof LlmPurposeSchema>;

/** The two tiers §13.3 describes. `deterministic` is not a tier — it is no call. */
export const MODEL_TIERS = ['strong', 'economy'] as const;
export const ModelTierSchema = z.enum(MODEL_TIERS);
export type ModelTier = z.infer<typeof ModelTierSchema>;

// ---------------------------------------------------------------------------
// Domains
// ---------------------------------------------------------------------------

/**
 * All six domains the model must accommodate without redesign. MVP ships three
 * (see `MVP_DOMAINS`); the rest exist so packs and schemas never need surgery.
 */
export const DOMAINS = ['it', 'privacy', 'ai', 'supply_chain', 'iot', 'ot'] as const;
export const DomainSchema = z.enum(DOMAINS);
export type Domain = z.infer<typeof DomainSchema>;

/**
 * The shipping set for MVP (CLAUDE.md §MVP scope) — and the *default* set a
 * tenant is enabled for, not the gate. Which domains a given tenant may select
 * is `tenants.enabled_domains_json` (docs/DECISIONS.md ZD-010); `isMvpDomain`
 * exists for seeding that default and for anywhere no tenant is in scope yet.
 */
export const MVP_DOMAINS = ['it', 'privacy', 'ai'] as const satisfies readonly Domain[];
export type MvpDomain = (typeof MVP_DOMAINS)[number];

export function isMvpDomain(domain: Domain): domain is MvpDomain {
  return (MVP_DOMAINS as readonly Domain[]).includes(domain);
}

/**
 * What a client is told a domain is, when asked to confirm that it applies to
 * their system (docs/DECISIONS.md ZD-121 §6, ZD-124 §2).
 *
 * The keys are the vocabulary above; the words are for a person who is
 * confirming their own scope and cannot be asked to know what a pack is. Each
 * sentence says what the domain is *about* and nothing about how it is
 * analysed — lenses, patterns and packs are the product's, not the client's.
 * Client-facing, so `enums.test.ts` holds each to the internal-token rule.
 */
export const DOMAIN_LABELS: Readonly<Record<Domain, { label: string; about: string }>> = {
  it: {
    label: 'IT',
    about: 'The system’s services, networks, identities and the data that moves between them.',
  },
  privacy: {
    label: 'Privacy',
    about: 'Personal data the system holds or processes, and the people it is about.',
  },
  ai: {
    label: 'AI',
    about: 'Models, prompts, agents and the tools and data they are given.',
  },
  supply_chain: {
    label: 'Supply chain',
    about: 'Third-party components, services and the trust the system places in them.',
  },
  iot: {
    label: 'IoT',
    about: 'Connected devices, their firmware and the channels they report over.',
  },
  ot: {
    label: 'OT',
    about: 'Industrial control systems and the physical processes they operate.',
  },
};

// ---------------------------------------------------------------------------
// Risk vocabulary
// ---------------------------------------------------------------------------

/** STRIDE classifies; scoring scores. They are orthogonal (DESIGN_SPEC §9). */
export const STRIDE_CATEGORIES = ['S', 'T', 'R', 'I', 'D', 'E'] as const;
export const StrideCategorySchema = z.enum(STRIDE_CATEGORIES);
export type StrideCategory = z.infer<typeof StrideCategorySchema>;

export const STRIDE_LABELS: Record<StrideCategory, string> = {
  S: 'Spoofing',
  T: 'Tampering',
  R: 'Repudiation',
  I: 'Information Disclosure',
  D: 'Denial of Service',
  E: 'Elevation of Privilege',
};

/**
 * The analysis framework a threat pattern reasons within (docs/DECISIONS.md
 * ZD-009). STRIDE is the IT lens, not the universal one — `stride_category` on
 * `ThreatPattern` is required only when `analysis_lens === 'stride'`.
 *
 * MVP members are the lenses IT, Privacy and AI packs actually use. The rest
 * are reserved for the follow-on domains (supply chain, IoT, OT) so the enum
 * never needs surgery when those packs land.
 */
export const ANALYSIS_LENSES = [
  // MVP
  'stride',
  'mitre_atlas',
  'owasp_llm_top_10',
  'ai_lifecycle',
  'agent_tool_use',
  'rag_security',
  'privacy_harms',
  'linddun_style',
  'data_protection_by_design',
  // Reserved — supply chain, IoT, OT
  'dependency',
  'provenance',
  'cicd',
  'third_party',
  'device_lifecycle',
  'iot_ecosystem',
  'iec_62443',
  'attack_ics',
  'safety_process_impact',
] as const;
export const AnalysisLensSchema = z.enum(ANALYSIS_LENSES);
export type AnalysisLens = z.infer<typeof AnalysisLensSchema>;

/** The normalised band. The pipeline, review and reports read this and never the method. */
export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'informational'] as const;
export const SeveritySchema = z.enum(SEVERITIES);
export type Severity = z.infer<typeof SeveritySchema>;

/**
 * Scoring methods behind the normalised band (DESIGN_SPEC §9.1).
 *
 * `unscored` is not a method — it is the honest label for a finding nothing has
 * scored (docs/DECISIONS.md ZD-005).
 *
 * **This said the guard on `scoring_complete` refuses to advance while any
 * finding still holds it, so it cannot reach a report.** That was true until
 * `sds-05y` deleted the stage (ZD-093), and the sentence is corrected rather
 * than dated, because it described a mechanism that no longer exists. What
 * keeps the label off a client's report is now two things, neither of them a
 * transition: the production path bands every finding it persists, pinned by
 * `finding-store.test.ts`, and a report that somehow received one **says so** —
 * `riskSection` renders `unavailable` and names the count rather than printing a
 * band nobody computed.
 */
export const SCORING_METHODS = [
  'unscored',
  'qrs_materiality',
  'dread',
  'owasp_rr',
  'qualitative',
] as const;
export const ScoringMethodSchema = z.enum(SCORING_METHODS);
export type ScoringMethod = z.infer<typeof ScoringMethodSchema>;

/** CLAUDE.md §Core conventions: QRS materiality is the default method. */
export const DEFAULT_SCORING_METHOD = 'qrs_materiality' satisfies ScoringMethod;

/** Confidence is derived from structural signals, never self-reported (§8.4). */
export const CONFIDENCES = ['low', 'medium', 'high'] as const;
export const ConfidenceSchema = z.enum(CONFIDENCES);
export type Confidence = z.infer<typeof ConfidenceSchema>;

export const PRIORITIES = ['high', 'medium', 'low'] as const;
export const PrioritySchema = z.enum(PRIORITIES);
export type Priority = z.infer<typeof PrioritySchema>;
/**
 * What sort of gap an `Unknown` reports (docs/DECISIONS.md ZD-057 §5, ZD-105 §7,
 * ZD-106).
 *
 * **The partition is on the gap's *subject*, not on its cause**, and that is
 * what makes it closable with no `other` member. A worker reading a document
 * finds a gap about one of six things: an object it found, a kind of object it
 * did not find, a name it cannot resolve, the document itself, two sources that
 * disagree, or two objects it cannot tell apart. Causes are open-ended;
 * subjects are not.
 *
 * **No `other`, deliberately, and the reason is this file's own `assumed`
 * argument one field along** (ZD-106 §2). An `other` member is the cheapest
 * thing for a fluent model to reach for and has no population behind it to make
 * over-use visible — the same door `assumed` was, in the same session that
 * closed it. Where a producer's reason genuinely fits none of these, the
 * vocabulary is extended by a decision, in code, in the session that adds the
 * producer.
 *
 * ZD-019's mirror question answers safely here, and for a reason that does not
 * generalise to an extracted attribute: the producer is not reading this off a
 * document, it is reporting **its own reason for raising the unknown**, so
 * there is no *"the source did not say"* case to manufacture an answer for.
 *
 * Measured across the 71 unknowns this corpus produces — 58 projected from
 * ground truth, 13 written by the pipeline (`sds-02c1`):
 *
 * | kind | of 58 | of 13 |
 * |---|---:|---:|
 * | `attribute_not_stated` | 49 | 0 |
 * | `object_not_found` | 6 | 5 |
 * | `reference_unresolved` | 1 | 6 |
 * | `artefact_unreadable` | 2 | 0 |
 * | `sources_disagree` | 0 | 2 |
 * | `identity_uncertain` | 0 | 0 |
 *
 * **The two populations are nearly disjoint, and that is the evidence the
 * vocabulary has more than one real member.** 49 of 58 in one bucket looks like
 * a vocabulary with a single member until the other population is counted: 0 of
 * the 13 the pipeline writes are `attribute_not_stated`. The 58's concentration
 * is a property of per-document ground truth, whose unit of labelling *is* the
 * missing attribute — and 9 of those 49 are plural-scoped (`all_flows.encrypted`
 * covering six flows in one row), which the real extractor writes as six.
 *
 * `identity_uncertain` has no instance in the pinned 71 and two live producers
 * (`normalise.ts`), because no merge is refused on this corpus; the judged
 * identity path takes unknowns 71 → 80 net (`identity-applied.test.ts`).
 */
export const UNKNOWN_KINDS = [
  /** An object was found and a named attribute of it was not stated. */
  'attribute_not_stated',
  /** Nothing of an expected kind was found at all, so there is no object to hang it on. */
  'object_not_found',
  /** Something is named and nothing in the documents identifies what it refers to. */
  'reference_unresolved',
  /** A document, or part of one, could not be read (docs/DECISIONS.md ZD-025). */
  'artefact_unreadable',
  /** Two sources describe one thing differently. Not available to extraction — see below. */
  'sources_disagree',
  /** Two objects may be one and the run could not tell (docs/DECISIONS.md ZD-026, ZD-048). */
  'identity_uncertain',
] as const;
export const UnknownKindSchema = z.enum(UNKNOWN_KINDS);
export type UnknownKind = z.infer<typeof UnknownKindSchema>;

/**
 * The kinds an **extraction** may raise, which is a layer refusal rather than a
 * subset chosen for tidiness (docs/DECISIONS.md ZD-106 §3).
 *
 * One extraction reads one artefact's content — `extractFromStructured` takes a
 * single `artefactId`, and `artefact_ids` is wider only so coverage can be
 * reported for artefacts the run accounted for (ZD-025). **One source cannot
 * disagree with itself**, so `sources_disagree` is not a kind an extraction can
 * honestly write; assembling a disagreement is normalisation's job, exactly as
 * it is for a `Conflict` record.
 *
 * Refused at the **shape** rather than by an integrity code, which is the
 * opposite of how the sibling `assumed` refusal lands, and the asymmetry is
 * argued rather than symmetric (ZD-106 §3). `ExtractedUnknown` is already a
 * separate schema from `Unknown`, so narrowing costs nothing and buys a
 * compile-time refusal; `Provenance` is deliberately **one object at both
 * layers** (ZD-014), so narrowing its `basis` would mean a second provenance
 * type rippling through `Provenanced`, `attributeProvenanceSchema` and the
 * `AssertProvenanced` assertions — a large surface, to unpick the property
 * ZD-014 exists to hold.
 *
 * A narrowed enum is a subtype, so normalisation lifting an extracted unknown
 * into a canonical one still typechecks with nothing to convert.
 */
export const EXTRACTION_UNKNOWN_KINDS = [
  'attribute_not_stated',
  'object_not_found',
  'reference_unresolved',
  'artefact_unreadable',
  'identity_uncertain',
] as const satisfies readonly UnknownKind[];
export const ExtractionUnknownKindSchema = z.enum(EXTRACTION_UNKNOWN_KINDS);

// ---------------------------------------------------------------------------
// Architecture vocabulary
// ---------------------------------------------------------------------------

export const COMPONENT_TYPES = [
  'web',
  'api',
  'db',
  'queue',
  'auth',
  'external',
  'mobile',
  'cdn',
  'cache',
  'storage',
  // AI domain (MVP — docs/DECISIONS.md ZD-010): the AI domain ships in MVP and
  // its patterns need somewhere in the architecture to ground.
  'ml_model',
  'llm_endpoint',
  'vector_store',
  'agent_tool',
  // Reserved — IoT and OT, unused until those packs land.
  'device',
  'controller',
  'gateway',
  'sensor',
  'hmi',
  'historian',
  /**
   * These two are not synonyms, and the distinction is load-bearing
   * (docs/DECISIONS.md ZD-019):
   *
   *  - `other` — the document **states** what this is and the enum has no
   *    member for it. A load balancer, a scheduled batch job. A real claim.
   *  - `unknown` — the document **does not say**. No claim at all.
   *
   * Without `unknown`, a worker that could not determine a type had to write
   * `other`, which asserts the first while meaning the second. Same reason
   * `TRUST_LEVELS` and `DATA_CLASSIFICATIONS` each carry one.
   */
  'other',
  'unknown',
] as const;
export const ComponentTypeSchema = z.enum(COMPONENT_TYPES);
export type ComponentType = z.infer<typeof ComponentTypeSchema>;

export const TRUST_LEVELS = ['internal', 'external', 'unknown'] as const;
export const TrustLevelSchema = z.enum(TRUST_LEVELS);
export type TrustLevel = z.infer<typeof TrustLevelSchema>;

export const DATA_CLASSIFICATIONS = [
  'public',
  'internal',
  'pii',
  'financial',
  'health',
  'secret',
  'unknown',
] as const;
export const DataClassificationSchema = z.enum(DATA_CLASSIFICATIONS);
export type DataClassification = z.infer<typeof DataClassificationSchema>;

/** Classifications the privacy-routing layer treats as sensitive (DESIGN_SPEC B.3). */
export const SENSITIVE_DATA_CLASSIFICATIONS = [
  'pii',
  'financial',
  'health',
  'secret',
] as const satisfies readonly DataClassification[];

/**
 * Whose data an asset concerns — free text, never a reference and never a
 * closed vocabulary (docs/DECISIONS.md ZD-076 §2). Shared by extraction and
 * canonical, imported by both the same way `DataClassificationSchema` is,
 * rather than declared twice.
 *
 * **A reference to an `Actor` was considered and refused.** Culverhay's
 * `Driver personal details` names "driver" in the document; the only
 * comparable actor is `hauliers`, the haulage firm, not the individual driver
 * who works for it. Forcing a reference would make an extractor decide the
 * two are the same object, which is exactly the resolution judgement
 * `sds-04e`/`sds-04h` already refused to make for Culverhay's plural
 * `yard controllers` (docs/BUILD_PLAN.md, sds-04n row, ZD-045 §2) — a
 * judgement declined per instance is not a safer judgement made once, in a
 * schema, for every document.
 *
 * **A closed vocabulary was refused for the same reason, one layer up.**
 * Mapping "passenger", "driver" and Vetchford's "customer" — which the source
 * text uses for two different roles in one document — onto a shared small
 * taxonomy is itself the judgement a fixed enum would be asked to absorb
 * silently. Free text transcribes what the document said, the same posture
 * `description` already takes on every canonical object.
 *
 * **Three-valued, matching `RiskDimension`** (`./scoring.ts`) rather than a
 * plain nullable string, because a bare `null` is the ZD-019 trapdoor an
 * empty string already is for `TrustBoundary.name`: two absences need two
 * words. `unestablished` is the document not saying; `not_applicable` is the
 * asset not being personal data about anyone — a secret, an API key, internal
 * documentation. **`not_applicable` has no producer yet, stated rather than
 * left to be discovered**, the same posture `RiskDimension`'s own comment
 * takes about itself: deciding which non-personal assets earn it is a
 * judgement this session did not make, having measured only whether a
 * document *states* a subject (ZD-076 §2), not whether one is absent for
 * cause. Measured across all four corpus systems: zero of the eight sensitive
 * personal-data assets are genuinely silent — `unestablished` exists for the
 * document nobody has written yet (ZD-036's absence-marker discipline,
 * applied here).
 */
export const DATA_SUBJECT_ABSENCES = ['unestablished', 'not_applicable'] as const;
export const DataSubjectAbsenceSchema = z.enum(DATA_SUBJECT_ABSENCES);
export type DataSubjectAbsence = z.infer<typeof DataSubjectAbsenceSchema>;

export const DataSubjectSchema = z.union([NonEmptyStringSchema, DataSubjectAbsenceSchema]);
export type DataSubject = z.infer<typeof DataSubjectSchema>;

export const TRUST_BOUNDARY_KINDS = [
  'network',
  'tenancy',
  'process',
  'organisational',
  'physical',
  // Reserved — IEC 62443 zone/conduit modelling for OT (docs/DECISIONS.md ZD-010).
  'zone',
  'conduit',
  /**
   * The same distinction `COMPONENT_TYPES` carries, for the same reason
   * (docs/DECISIONS.md ZD-019, ZD-021):
   *
   *  - `other` — the document **states** what kind of boundary this is and the
   *    enum has no member for it. A real claim.
   *  - `unknown` — nothing said. No input format states a boundary kind at all,
   *    so this is what normalisation writes for a boundary read off a diagram,
   *    and `other` would assert a claim nobody made.
   */
  'other',
  'unknown',
] as const;
export const TrustBoundaryKindSchema = z.enum(TRUST_BOUNDARY_KINDS);
export type TrustBoundaryKind = z.infer<typeof TrustBoundaryKindSchema>;

export const ENVIRONMENT_KINDS = [
  'production',
  'staging',
  'development',
  'test',
  'dr',
  'other',
] as const;
export const EnvironmentKindSchema = z.enum(ENVIRONMENT_KINDS);
export type EnvironmentKind = z.infer<typeof EnvironmentKindSchema>;

/**
 * What an observed control actually does (docs/DECISIONS.md ZD-039).
 *
 * **This exists because its absence was a false-negative mechanism, not a
 * missing field.** Until it landed, every pattern of the form *"no observed
 * control applies to this"* was satisfied by **any** control, so a quarterly
 * restore-from-backup rehearsal suppressed the data-protection finding on
 * Culverhay's reporting database and the finding fired on the haulier
 * directory instead — a report directing a client away from a real exposure on
 * the strength of a control they correctly hold (ZD-037 §4).
 *
 * ### It is a collection, and that is the decision rather than a detail
 *
 * A scalar would fail ZD-021's mirror question. The Tarnmouth LLD says
 * *"Accepts mutual TLS only; client certificates are issued by the internal
 * CA"* — mutual TLS is authentication **and** encryption in transit, and a
 * scalar has one slot for a two-part fact, so the second half would have
 * nowhere to go and the next layer would read `unknown` over something the
 * document stated. That is the same shape as the still-open
 * `one-classification-per-asset` gap, and shipping a second knowing instance of
 * it in the session that exists to close a ZD-021 gap is not defensible.
 *
 * Three things then arrive with no new rule, which is what a correct shape
 * looks like: an unestablished collection evaluates to `unknown` (ZD-029 §3),
 * so *"the document names a control and does not say what it does"* routes to
 * clarification rather than to a claim in either direction; ZD-036 §2's ban on
 * establishing an empty collection applies automatically, because its scope is
 * read off the schema shape; and merging two documents' accounts unions rather
 * than conflicts (ZD-026 §3).
 *
 * ### There is no `unknown` member, deliberately
 *
 * Unestablished **is** the absence. A member as well would spell one state two
 * ways, which is exactly the trapdoor ZD-036 §2 exists to close one level down.
 * `other` stays, on ZD-019's `COMPONENT_TYPES` precedent: it means the document
 * states a purpose this vocabulary cannot hold, which is a claim. No pattern
 * may list `other` (`pack.ts` refuses it), so `other` can never suppress a
 * finding — the safe direction.
 *
 * ### These are permanent anchors
 *
 * A threat pattern names them, so renaming one is a breaking change to every
 * pattern that reads it, for ZD-004's reason.
 */
export const CONTROL_KINDS = [
  /** TLS, mutual TLS's transport half, VPN. Not anything at rest. */
  'encryption_in_transit',
  /** Disk, bucket or database encryption. Not the key management serving it, and not backups. */
  'encryption_at_rest',
  /** Secrets managers, KMS and customer-managed keys, rotation. Not the encryption the key serves. */
  'key_and_secret_management',
  /** SSO, MFA, client certificates, API keys as credentials. Not what the party may then do. */
  'authentication',
  /** Least privilege, tenant scoping, per-tool permission constraints. Not proving identity. */
  'authorisation',
  /** Firewalls, WAFs, IP allowlists, blocked public access, segmentation, egress restriction. */
  'network_filtering',
  /** Bounds on request volume, concurrency or cost. */
  'rate_limiting',
  /** Event recording, audit trail, log integrity, alerting, detection. Not retention of business data. */
  'logging_and_monitoring',
  /** Delimiting untrusted content, validating index or training sources, filtering model output. */
  'content_validation',
  /** A person must approve before the action executes. Not automated authorisation. */
  'human_approval',
  /** Tokenisation, pseudonymisation, redaction, field minimisation. Not deletion after a period. */
  'data_minimisation',
  /** Retention limits, lifecycle policies, deletion capability. Not minimisation, and not backups. */
  'retention_and_deletion',
  /** Backups, restore rehearsals, failover, isolation of shared dependencies. */
  'availability_and_recovery',
  /** Agreements, contracts, recorded lawful basis, classification policy. No technical mechanism. */
  'governance',
  /** The document states a purpose this vocabulary cannot hold. A claim, unlike an absence. */
  'other',
] as const;
export const ControlKindSchema = z.enum(CONTROL_KINDS);
export type ControlKind = z.infer<typeof ControlKindSchema>;

/** Which plane a control flow belongs to (docs/DECISIONS.md ZD-006). */
export const CONTROL_PLANES = [
  'deployment',
  'administration',
  'configuration',
  'key_management',
  'telemetry',
  'other',
] as const;
export const ControlPlaneSchema = z.enum(CONTROL_PLANES);
export type ControlPlane = z.infer<typeof ControlPlaneSchema>;

/**
 * Object kinds a threat pattern may require and a finding may ground on.
 * Mirrors the canonical model's collections.
 *
 * This is the expensive list (docs/DECISIONS.md ZD-010): it is referenced by
 * `applicability.requires_object_types` and by the grounding rules, so an
 * addition after packs exist is a breaking change. `safety_function` is
 * reserved for the OT pack and is the one addition made ahead of time —
 * everything else IoT and OT need is expressible as component types or
 * trust-boundary kinds.
 */
export const CANONICAL_OBJECT_TYPES = [
  'component',
  'actor',
  'asset',
  'data_flow',
  'control_flow',
  'trust_boundary',
  'environment',
  'existing_control',
  'safety_function',
] as const;
export const CanonicalObjectTypeSchema = z.enum(CANONICAL_OBJECT_TYPES);
export type CanonicalObjectType = z.infer<typeof CanonicalObjectTypeSchema>;

/**
 * CLAUDE.md principle 4: a finding must cite at least one *real canonical object
 * id (component / flow / boundary / asset)*. Actors, environments and observed
 * controls may be cited in addition, but never on their own — a threat that
 * grounds only on an actor is not grounded in the architecture.
 */
export const PRIMARY_GROUNDING_TYPES = [
  'component',
  'asset',
  'data_flow',
  'control_flow',
  'trust_boundary',
] as const satisfies readonly CanonicalObjectType[];

/**
 * How the applicability predicate was satisfied — the structural input to
 * derived confidence (DESIGN_SPEC §8.4).
 */
export const PREDICATE_BASES = ['core_attribute', 'boost', 'inferred', 'assumed'] as const;
export const PredicateBasisSchema = z.enum(PREDICATE_BASES);
export type PredicateBasis = z.infer<typeof PredicateBasisSchema>;

/**
 * **Who wrote the sentence** (docs/DECISIONS.md ZD-031, ZD-061 §2).
 *
 * `rendered` — a transcript of the finding's own witnesses, produced in code:
 * every clause is a fact in the canonical model with a citation behind it.
 * `authored` — contextualised by a model, which is a different kind of claim
 * and has to be visible as one. `approver_edited` — a person at the human gate
 * rewrote it. None is a lesser state; conflating any two is what would be wrong,
 * because sds-09 scores them differently and a replacement would otherwise be
 * invisible.
 *
 * **The third member arrives before anything can write it, deliberately.** With
 * two members, a sentence a reviewer typed would have to be stored as
 * `authored` — a false claim, indistinguishable downstream from the true one,
 * and unrecoverable once findings are stored under it (ZD-021's retrofit cost,
 * and the sixth instance of the two-axes family CLAUDE.md tracks). Asking the
 * question of a field being *added* costs a member with no producer; asking it
 * of one already written costs a migration.
 *
 * `approver_edited` rather than `edited`, because `edited` is a **finding
 * state** — a property of the whole finding — and this is a property of one
 * slot. And rather than reusing `authored`, because *which* of the two wrote a
 * sentence is precisely what a client-facing provenance statement has to say.
 *
 * **This is not the same axis as `Provenance.asserted_by_run_id`** (ZD-046 §7).
 * That says who established the *fact*; this says who wrote the *sentence*. A
 * slot rendering a fact an LLM extractor established is still `rendered`.
 */
export const PROSE_ORIGINS = ['rendered', 'authored', 'approver_edited'] as const;
export const ProseOriginSchema = z.enum(PROSE_ORIGINS);
export type ProseOrigin = z.infer<typeof ProseOriginSchema>;

/**
 * Where an attribute's value came from. `labelled` means a human or artefact
 * stated it. `contradicted` means two sources disagree — see `Conflict`
 * (docs/DECISIONS.md ZD-013): a contradiction forces confidence to `low` and
 * must have a matching conflict record on the model.
 */
export const PROVENANCE_BASES = ['labelled', 'inferred', 'assumed', 'contradicted'] as const;
export const ProvenanceBasisSchema = z.enum(PROVENANCE_BASES);
export type ProvenanceBasis = z.infer<typeof ProvenanceBasisSchema>;

// ---------------------------------------------------------------------------
// Tenancy, input and output
// ---------------------------------------------------------------------------

export const TENANT_TIERS = ['shared', 'dedicated'] as const;
export const TenantTierSchema = z.enum(TENANT_TIERS);
export type TenantTier = z.infer<typeof TenantTierSchema>;

export const APPROVER_POLICIES = ['qrs', 'client_role', 'either'] as const;
export const ApproverPolicySchema = z.enum(APPROVER_POLICIES);
export type ApproverPolicy = z.infer<typeof ApproverPolicySchema>;

export const LLM_MODES = ['managed', 'dedicated', 'no_llm'] as const;
export const LlmModeSchema = z.enum(LLM_MODES);
export type LlmMode = z.infer<typeof LlmModeSchema>;

/** In-app roles. `approver` is the capability the mandatory human gate requires. */
export const USER_ROLES = ['owner', 'admin', 'approver', 'analyst', 'viewer'] as const;
export const UserRoleSchema = z.enum(USER_ROLES);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const ARTEFACT_KINDS = ['diagram_image', 'pdf', 'text', 'structured'] as const;
export const ArtefactKindSchema = z.enum(ARTEFACT_KINDS);
export type ArtefactKind = z.infer<typeof ArtefactKindSchema>;

export const REPORT_TYPES = [
  'executive',
  'technical',
  'threat_register',
  'risk_register',
  'recommendations',
  'requirements',
  'board_summary',
] as const;
export const ReportTypeSchema = z.enum(REPORT_TYPES);
export type ReportType = z.infer<typeof ReportTypeSchema>;

export const REPORT_FORMATS = ['pdf', 'docx', 'xlsx', 'json', 'md'] as const;
export const ReportFormatSchema = z.enum(REPORT_FORMATS);
export type ReportFormat = z.infer<typeof ReportFormatSchema>;

export const ACTION_STATUSES = ['open', 'in_progress', 'done', 'accepted_risk'] as const;
export const ActionStatusSchema = z.enum(ACTION_STATUSES);
export type ActionStatus = z.infer<typeof ActionStatusSchema>;

export const EXTERNAL_SYSTEMS = ['jira', 'ado', 'github'] as const;
export const ExternalSystemSchema = z.enum(EXTERNAL_SYSTEMS);
export type ExternalSystem = z.infer<typeof ExternalSystemSchema>;

export const IMPLEMENTATION_OWNER_TYPES = [
  'Engineering',
  'Security',
  'Platform',
  'Product',
] as const;
export const ImplementationOwnerTypeSchema = z.enum(IMPLEMENTATION_OWNER_TYPES);
export type ImplementationOwnerType = z.infer<typeof ImplementationOwnerTypeSchema>;

/** Contents of the immutable evidence bundle (DESIGN_SPEC §7.9). */
export const EVIDENCE_CONTENTS = [
  'original_artefacts',
  'extracted_model',
  'canonical_model',
  'assumptions_register',
  'clarification_qa',
  'approved_findings',
  'risk_register',
  'recommendation_register',
  'control_mappings',
  'review_log',
  'report_outputs',
  'model_version',
  'pack_versions',
  'llm_config',
  'reviewer_identity_and_date',
] as const;
export const EvidenceContentSchema = z.enum(EVIDENCE_CONTENTS);
export type EvidenceContent = z.infer<typeof EvidenceContentSchema>;

// ---------------------------------------------------------------------------
// Audit and runs
// ---------------------------------------------------------------------------

/**
 * Who acted. The distinction is load-bearing: the guard on
 * `approved_for_reporting` requires an actor of kind `user`, which is what makes
 * the human gate unbypassable by any worker (docs/DECISIONS.md ZD-007).
 */
export const ACTOR_KINDS = ['user', 'worker', 'orchestrator', 'system'] as const;
export const ActorKindSchema = z.enum(ACTOR_KINDS);
export type ActorKind = z.infer<typeof ActorKindSchema>;

export const AGENT_RUN_STATUSES = ['ok', 'rejected', 'error'] as const;
export const AgentRunStatusSchema = z.enum(AGENT_RUN_STATUSES);
export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>;

export const REVIEW_DECISIONS = ['approved', 'changes_required', 'rejected'] as const;
export const ReviewDecisionSchema = z.enum(REVIEW_DECISIONS);
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>;

export const QA_CHECK_RESULTS = ['pass', 'fail', 'not_applicable'] as const;
export const QaCheckResultSchema = z.enum(QA_CHECK_RESULTS);
export type QaCheckResult = z.infer<typeof QaCheckResultSchema>;

/**
 * Actions a worker contract can forbid (DESIGN_SPEC §6.1). A closed list rather
 * than free text, so `assertNotForbidden` can actually enforce them.
 */
export const FORBIDDEN_ACTIONS = [
  'generate_threats',
  'generate_recommendations',
  'create_findings',
  'mark_assumption_as_fact',
  'produce_report_prose',
  'promote_finding_to_approved',
  'promote_finding_to_client_ready',
  'alter_severity',
  'invent_control',
  'transition_assessment_state',
  'read_other_tenant_data',
  'call_external_tool',
] as const;
export const ForbiddenActionSchema = z.enum(FORBIDDEN_ACTIONS);
export type ForbiddenAction = z.infer<typeof ForbiddenActionSchema>;

/** Frameworks QRS maps to today. Open-ended by design — mappings carry a string. */
export const KNOWN_FRAMEWORKS = [
  'ISO 27001',
  'NCSC CAF',
  'DORA',
  'NIST CSF',
  'Cyber Essentials',
] as const;
